import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radar, X, ArrowRight } from "lucide-react";
import { useI18n, formatRelative, itemDate } from "@/i18n";
import { useLiveFeed, desktopAlertsOn } from "@/components/NotificationCenter";
import { CategoryIcon } from "@/components/CategoryTag";

export function LiveNotifier() {
  const { t, lang, title } = useI18n();
  const seen = useRef(new Set());
  const inited = useRef(false);
  const timer = useRef(null);
  const [note, setNote] = useState(null);
  const [leaving, setLeaving] = useState(false);

  const { data, isSuccess } = useLiveFeed();

  const dismiss = useCallback(() => {
    setLeaving(true);
    setTimeout(() => { setNote(null); setLeaving(false); }, 300);
  }, []);

  // Initialize the 'seen' set on the first settled response (even if empty)
  useEffect(() => {
    if (isSuccess && !inited.current) {
      (data?.items || []).forEach((i) => seen.current.add(i.id));
      inited.current = true;
    }
  }, [isSuccess, data]);

  // Notify for genuinely new items that arrive after initialization
  useEffect(() => {
    if (!inited.current) return;
    const items = data?.items || [];
    const fresh = items.find((i) => !seen.current.has(i.id));
    items.forEach((i) => seen.current.add(i.id));
    if (fresh) {
      if (document.hidden && desktopAlertsOn()) {
        const n = new Notification(title(fresh), { body: `${fresh.source_name} · NxtTechNews`, icon: "/logo.png", tag: fresh.id });
        n.onclick = () => { window.focus(); window.location.href = `/article/${fresh.id}`; };
      }
      setLeaving(false);
      setNote(fresh);
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => dismiss(), 9000);
    }
  }, [data, dismiss, title]);

  // Clear the auto-dismiss timer only on unmount
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (!note) return null;

  return (
    <div
      data-testid="live-notification"
      className={`fixed z-[70] bottom-4 start-4 w-[340px] max-w-[calc(100vw-2rem)] rounded-2xl p-[1.5px] glow-border shadow-[0_18px_50px_rgba(76,29,149,0.35)] ${leaving ? "notify-out" : "notify-pop"}`}
    >
      <div className="relative rounded-[14px] bg-white overflow-hidden">
        <span className="scan-line" />
        <div className="relative p-4">
          <div className="flex items-center justify-between mb-2.5">
            <span className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-violet-700">
              <span className="live-ring w-1.5 h-1.5 rounded-full bg-violet-600" />
              <Radar size={12} className="radar-spin" /> {t("notify.breaking")}
            </span>
            <button data-testid="notify-dismiss" onClick={dismiss} aria-label={t("notify.dismiss")} className="text-slate-400 hover:text-slate-700 transition-colors">
              <X size={15} />
            </button>
          </div>
          <Link to={`/article/${note.id}`} onClick={dismiss} className="flex gap-3 group">
            <span className="shrink-0 w-9 h-9 rounded-xl bg-violet-600 text-white inline-flex items-center justify-center"><CategoryIcon slug={note.categories?.[0]} size={17} /></span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-slate-800 leading-snug line-clamp-3 group-hover:text-violet-700 transition-colors">{title(note)}</span>
              <span className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
                <span className="font-medium text-slate-600">{note.source_name} · {formatRelative(itemDate(note), lang)}</span>
                <span className="inline-flex items-center gap-1 text-violet-700 font-medium">{t("notify.read")} <ArrowRight size={12} className="rtl:rotate-180" /></span>
              </span>
            </span>
          </Link>
        </div>
        <div className="h-0.5 bg-violet-100"><div className="h-full bg-violet-600 notify-progress" /></div>
      </div>
    </div>
  );
}
